import { toIsoDate, displayDateUS } from "../../utils/dateUtils";
import {
  serializeNutrition,
  validateNutritionFields,
} from "./patientNutrition.shared";

export const INSURANCE_OPTIONS = [
  { value: "Blue Cross Blue Shield", label: "Blue Cross Blue Shield" },
  { value: "Aetna", label: "Aetna" },
  { value: "UnitedHealthcare", label: "UnitedHealthcare" },
  { value: "Cigna", label: "Cigna" },
  { value: "Humana", label: "Humana" },
];

export const REFERRAL_SOURCE_OPTIONS = [
  { value: "Hospital Discharge", label: "Hospital Discharge" },
  { value: "Community Referral", label: "Community Referral" },
  { value: "Physician Referral", label: "Physician Referral" },
  { value: "Other", label: "Other" },
];

/** Common allergy names only (dropdown). */
export const ALLERGY_OTHER_VALUE = "Other";

export const COMMON_ALLERGY_OPTIONS = [
  { value: "NKDA", label: "NKDA (No Known Drug Allergies)" },
  { value: "Penicillin", label: "Penicillin" },
  { value: "Sulfa", label: "Sulfa" },
  { value: "Aspirin", label: "Aspirin" },
  { value: "Ibuprofen", label: "Ibuprofen" },
  { value: "Codeine", label: "Codeine" },
  { value: "Morphine", label: "Morphine" },
  { value: "Latex", label: "Latex" },
  { value: "Iodine", label: "Iodine" },
  { value: "Contrast Dye", label: "Contrast Dye" },
  { value: "Peanuts", label: "Peanuts" },
  { value: "Shellfish", label: "Shellfish" },
  { value: "Eggs", label: "Eggs" },
  { value: "Milk", label: "Milk" },
  { value: "Soy", label: "Soy" },
  { value: "Wheat", label: "Wheat" },
  { value: ALLERGY_OTHER_VALUE, label: "Other" },
];

/** True when value is a fixed catalog option (not Other / custom). */
export const isCatalogAllergyValue = (value) => {
  const v = String(value || "").trim();
  if (!v || v === ALLERGY_OTHER_VALUE) return false;
  return COMMON_ALLERGY_OPTIONS.some((o) => o.value === v);
};

export const isOtherAllergySelection = (value) =>
  String(value || "").trim() === ALLERGY_OTHER_VALUE;

/**
 * Resolve UI row → persisted allergy string.
 * Other + empty custom text → null (skip on save).
 */
export const resolveAllergyName = (row = {}) => {
  const selection = String(row?.allergyName || "").trim();
  if (!selection) return null;
  if (isOtherAllergySelection(selection)) {
    const custom = String(row?.otherText || "").trim();
    if (!custom || custom === ALLERGY_OTHER_VALUE) return null;
    return custom.slice(0, 255);
  }
  return selection.slice(0, 255);
};

/** Payload rows from UI allergy state (deduped, Other resolved). */
export const serializeAllergies = (rows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  const seen = new Set();
  const out = [];
  for (const row of list) {
    const name = resolveAllergyName(row);
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ allergyName: name });
  }
  return out;
};

/** Incomplete Other rows (selected Other but no custom text). */
export const findIncompleteOtherAllergy = (rows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  for (let idx = 0; idx < list.length; idx += 1) {
    const row = list[idx];
    if (!isOtherAllergySelection(row?.allergyName)) continue;
    if (!String(row?.otherText || "").trim()) {
      return {
        index: idx,
        message: `Allergy ${idx + 1}: enter the custom allergy name`,
      };
    }
  }
  return null;
};

export const CLINICAL_GROUP_OPTIONS = [
  { value: "MMTA - Other", label: "MMTA - Other" },
  { value: "Neuro Rehabilitation", label: "Neuro Rehabilitation" },
  { value: "Wound", label: "Wound" },
  {
    value: "Complex Nursing Interventions",
    label: "Complex Nursing Interventions",
  },
  {
    value: "Musculoskeletal Rehabilitation",
    label: "Musculoskeletal Rehabilitation",
  },
  { value: "Behavioral Health", label: "Behavioral Health" },
  { value: "MMTA - Surgical Aftercare", label: "MMTA - Surgical Aftercare" },
  {
    value: "MMTA - Cardiac and Circulatory",
    label: "MMTA - Cardiac and Circulatory",
  },
  { value: "MMTA - Endocrine", label: "MMTA - Endocrine" },
  {
    value: "MMTA - Gastrointestinal tract and Genitourinary system",
    label: "MMTA - GI / GU",
  },
  {
    value:
      "MMTA - Infectious Disease, Neoplasms, and Blood-Forming Diseases",
    label: "MMTA - Infectious / Neoplasms / Blood",
  },
  { value: "MMTA - Respiratory", label: "MMTA - Respiratory" },
  { value: "No Group Assigned", label: "No Group Assigned" },
];

export const RELATIONSHIP_OPTIONS = [
  { value: "Spouse", label: "Spouse" },
  { value: "Parent", label: "Parent" },
  { value: "Child", label: "Child" },
  { value: "Sibling", label: "Sibling" },
  { value: "Brother", label: "Brother" },
  { value: "Sister", label: "Sister" },
  { value: "Friend", label: "Friend" },
  { value: "Guardian", label: "Guardian" },
  { value: "Other", label: "Other" },
];

export const MARITAL_STATUS_OPTIONS = [
  { value: "Single", label: "Single" },
  { value: "Married", label: "Married" },
  { value: "Divorced", label: "Divorced" },
  { value: "Widowed", label: "Widowed" },
  { value: "Separated", label: "Separated" },
  { value: "Domestic Partner", label: "Domestic Partner" },
  { value: "Other", label: "Other" },
];

export const PHYSICIAN_TYPE_OPTIONS = [
  { value: "Attending", label: "Attending" },
  { value: "Referring", label: "Referring" },
  { value: "Consulting", label: "Consulting" },
  { value: "Primary Care", label: "Primary Care" },
  { value: "Other", label: "Other" },
];

/** PECOS status — from physician_master.pmd (true → enrolled). */
export const PECOS_STATUS_OPTIONS = [
  { value: "enrolled", label: "Enrolled" },
  { value: "un-enrolled", label: "Un-enrolled" },
];

/** Map physician_master.pmd boolean → UI PECOS status. */
export const pecosStatusFromPmd = (pmd) =>
  pmd ? "enrolled" : "un-enrolled";

export const PHARMACY_TYPE_OPTIONS = [
  { value: "Retail", label: "Retail" },
  { value: "Mail Order", label: "Mail Order" },
  { value: "Specialty", label: "Specialty" },
  { value: "Long-term Care", label: "Long-term Care" },
  { value: "Compounding", label: "Compounding" },
  { value: "Hospital", label: "Hospital" },
  { value: "Other", label: "Other" },
];

/** Primary hospital type — aligned with Libraries care facility types. */
export const HOSPITAL_TYPE_OPTIONS = [
  { value: "Hospital", label: "Hospital" },
  { value: "SNF", label: "SNF" },
  { value: "ALF", label: "ALF" },
  { value: "Clinic", label: "Clinic" },
  { value: "Home Health", label: "Home Health" },
  { value: "Hospice", label: "Hospice" },
  { value: "Rehab", label: "Rehab" },
  { value: "Other", label: "Other" },
];

/** Common US race categories (per HHS/OMB reporting categories). */
export const RACE_OPTIONS = [
  { value: "American Indian or Alaska Native", label: "American Indian or Alaska Native" },
  { value: "Asian", label: "Asian" },
  { value: "Black or African American", label: "Black or African American" },
  { value: "Native Hawaiian or Other Pacific Islander", label: "Native Hawaiian or Other Pacific Islander" },
  { value: "White", label: "White" },
  { value: "Two or More Races", label: "Two or More Races" },
  { value: "Other", label: "Other" },
  { value: "Decline to Answer", label: "Decline to Answer" },
];

/** Common religion categories. */
export const RELIGION_OPTIONS = [
  { value: "Christianity", label: "Christianity" },
  { value: "Islam", label: "Islam" },
  { value: "Judaism", label: "Judaism" },
  { value: "Hinduism", label: "Hinduism" },
  { value: "Buddhism", label: "Buddhism" },
  { value: "Sikhism", label: "Sikhism" },
  { value: "Atheist/Agnostic", label: "Atheist/Agnostic" },
  { value: "Other", label: "Other" },
  { value: "Decline to Answer", label: "Decline to Answer" },
];

export const US_PHONE = /^\(\d{3}\)\d{3}-\d{4}$/;
export const US_ZIP = /^\d{5}(-\d{4})?$/;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Map API "Invalid body - physicians.0.email: …" into field error keys. */
export const parsePhysicianApiFieldErrors = (message) => {
  const text = String(message || "");
  const errors = {};
  const re = /physicians\.(\d+)\.(\w+):\s*([^,]+)/g;
  let match;
  while ((match = re.exec(text))) {
    const msg = match[3].trim();
    errors[`physicians.${match[1]}.${match[2]}`] =
      msg === "Invalid input" ? "Invalid value" : msg;
  }
  return errors;
};

export const formatZip = (value) => {
  if (!value) return "";
  const digits = String(value).replaceAll(/\D/g, "").slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export const splitZipParts = (value = "") => {
  const digits = String(value).replaceAll(/\D/g, "").slice(0, 9);
  return {
    base: digits.slice(0, 5),
    plus4: digits.slice(5, 9),
  };
};

export const joinZipParts = (base = "", plus4 = "") => {
  const b = String(base).replaceAll(/\D/g, "").slice(0, 5);
  const p = String(plus4).replaceAll(/\D/g, "").slice(0, 4);
  if (!b) return "";
  return p ? `${b}-${p}` : b;
};

/** Legacy fallback — splits a single freeform "First Last" name into parts. */
export const splitEmergencyName = (fullName = "") => {
  const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

export const emptyEmergencyContact = () => ({
  firstName: "",
  lastName: "",
  relationship: "",
  phone: "",
});

export const emptyDiagnosisAllergy = () => ({
  diagnosisCode: "",
  primaryDiagnosis: "",
  clinicalGroup: "",
  isBillable: false,
});

export const emptyAllergy = () => ({
  allergyName: "",
  otherText: "",
});

export const emptyReferral = () => ({
  referralDate: "",
  referralSource: "",
  referralName: "",
});

export const emptyInsurance = () => ({
  startDate: "",
  insuranceName: "",
  policyNumber: "",
  insuranceGroup: "",
  patientCoPay: "",
});

export const emptyPhysician = () => ({
  physicianType: "",
  physicianName: "",
  physicianAddress: "",
  npiNumber: "",
  pecosStatus: "",
  physicianPhone: "",
  physicianEmail: "",
  physicianFax: "",
});

/** Join allergy names for display / clinical prefills (deduped). */
export const formatAllergyNames = (rows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  const unique = [];
  for (const row of list) {
    const name =
      resolveAllergyName(row) || String(row?.allergyName || "").trim();
    if (!name || name === ALLERGY_OTHER_VALUE) continue;
    if (unique.some((u) => u.toLowerCase() === name.toLowerCase())) continue;
    unique.push(name);
  }
  return unique.join(", ");
};

/** @deprecated Legacy shared allergy on diagnosis rows — prefer patient.allergies. */
export const getSharedAllergyName = (rows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  const source =
    list.find((r) => String(r?.allergyName || "").trim()) || list[0] || {};
  return source.allergyName || "";
};

/** @deprecated */
export const withSharedAllergyName = (rows = [], allergyName = "") =>
  (Array.isArray(rows) && rows.length ? rows : [emptyDiagnosisAllergy()]).map(
    (r) => ({
      ...r,
      allergyName: allergyName || "",
    }),
  );

/** @deprecated Use getSharedAllergyName — clinical group is per diagnosis now. */
export const getSharedAllergyClinical = (rows = []) => ({
  allergyName: getSharedAllergyName(rows),
  clinicalGroup: "",
});

/** @deprecated Use withSharedAllergyName */
export const withSharedAllergyClinical = (rows = [], allergyName = "") =>
  withSharedAllergyName(rows, allergyName);

/** Normalizes patient.diagnosisAllergies (diagnosis rows only). */
export const buildDiagnosisAllergies = (patient = {}) => {
  if (Array.isArray(patient.diagnosisAllergies) && patient.diagnosisAllergies.length) {
    return patient.diagnosisAllergies.map((d) => ({
      diagnosisCode: d.diagnosisCode || "",
      primaryDiagnosis: d.primaryDiagnosis || "",
      clinicalGroup: d.clinicalGroup || "",
      isBillable:
        typeof d.isBillable === "boolean" ? d.isBillable : false,
    }));
  }
  return [];
};

/** Map a stored allergy string into edit-row shape (Other + otherText when custom). */
export const toAllergyEditRow = (allergyName = "") => {
  const name = String(allergyName || "").trim();
  if (!name) return emptyAllergy();
  if (isCatalogAllergyValue(name)) {
    return { allergyName: name, otherText: "" };
  }
  return { allergyName: ALLERGY_OTHER_VALUE, otherText: name };
};

/** Normalizes patient.allergies; falls back to legacy diagnosis-row allergyName. */
export const buildAllergies = (patient = {}) => {
  if (Array.isArray(patient.allergies) && patient.allergies.length) {
    return patient.allergies
      .map((a) => toAllergyEditRow(a?.allergyName))
      .filter(
        (a) =>
          a.allergyName &&
          !(
            isOtherAllergySelection(a.allergyName) &&
            !String(a.otherText || "").trim()
          ),
      );
  }
  const legacy = getSharedAllergyName(patient.diagnosisAllergies || []);
  if (legacy) return [toAllergyEditRow(legacy)];
  return [];
};

/** Normalizes patient.emergencyContacts (array) with a fallback to legacy single-contact fields. */
export const buildEmergencyContacts = (patient = {}) => {
  if (Array.isArray(patient.emergencyContacts) && patient.emergencyContacts.length) {
    return patient.emergencyContacts.map((c) => ({
      firstName: c.firstName || "",
      lastName: c.lastName || "",
      relationship: c.relationship || "",
      phone: c.phone || "",
    }));
  }

  const legacy = splitEmergencyName(patient.emergencyContactName || "");
  if (legacy.firstName || legacy.lastName || patient.emergencyContactPhone) {
    return [
      {
        firstName: legacy.firstName,
        lastName: legacy.lastName,
        relationship: patient.emergencyContactRelationship || "",
        phone: patient.emergencyContactPhone || "",
      },
    ];
  }

  return [];
};

/** Normalizes patient.referrals (array) with a fallback to legacy flat referralSource field. */
export const buildReferrals = (patient = {}) => {
  if (Array.isArray(patient.referrals) && patient.referrals.length) {
    return patient.referrals.map((r) => ({
      referralDate: displayDateUS(r.referralDate) || "",
      referralSource: r.referralSource || "",
      referralName: r.referralName || "",
    }));
  }
  if (patient.referralSource) {
    return [
      {
        referralDate: "",
        referralSource: patient.referralSource || "",
        referralName: "",
      },
    ];
  }
  return [];
};

/** Normalizes patient.insurances (array) with a fallback to legacy flat insurance/insuranceNumber fields. */
export const buildInsurances = (patient = {}) => {
  if (Array.isArray(patient.insurances) && patient.insurances.length) {
    return patient.insurances.map((i) => ({
      startDate: displayDateUS(i.startDate) || "",
      insuranceName: i.insuranceName || "",
      policyNumber: i.policyNumber || "",
      insuranceGroup: i.insuranceGroup || "",
      patientCoPay: i.patientCoPay || "",
    }));
  }
  if (patient.insurance || patient.insuranceNumber) {
    return [
      {
        startDate: "",
        insuranceName: patient.insurance || "",
        policyNumber: patient.insuranceNumber || "",
        insuranceGroup: "",
        patientCoPay: "",
      },
    ];
  }
  return [];
};

/** Normalizes patient.physicians (array) with a fallback to legacy flat primaryPhysician* fields. */
export const buildPhysicians = (patient = {}) => {
  if (Array.isArray(patient.physicians) && patient.physicians.length) {
    return patient.physicians.map((p) => ({
      physicianType: p.physicianType || "",
      physicianName: p.physicianName || "",
      physicianAddress: p.physicianAddress || "",
      npiNumber: p.npiNumber || "",
      pecosStatus: p.pecosStatus || "",
      physicianPhone: p.physicianPhone || "",
      physicianEmail: p.physicianEmail || "",
      physicianFax: p.physicianFax || "",
    }));
  }
  if (patient.primaryPhysician || patient.primaryPhysicianAddress) {
    return [
      {
        physicianType: "Primary Care",
        physicianName: patient.primaryPhysician || "",
        physicianAddress: patient.primaryPhysicianAddress || "",
        npiNumber: "",
        pecosStatus: "",
        physicianPhone: patient.primaryPhysicianPhone || "",
        physicianEmail: "",
        physicianFax: patient.primaryPhysicianFax || "",
      },
    ];
  }
  return [];
};

const nonEmpty = (v) => Boolean(String(v || "").trim());

export const SECTION_KEYS = {
  personal: [
    "lastName",
    "firstName",
    "middleName",
    "status",
    "admissionDate",
    "eocDate",
    "dateOfBirth",
    "gender",
    "language",
    "maritalStatus",
    "race",
    "religion",
  ],
  contact: [
    "phone",
    "alternatePhone",
    "email",
    "address",
    "addressLine2",
    "city",
    "state",
    "zip",
  ],
  pharmacy: [
    "pharmacyName",
    "pharmacyAddress",
    "pharmacyCity",
    "pharmacyState",
    "pharmacyZip",
    "pharmacyPhone",
    "pharmacyFax",
    "pharmacyType",
  ],
  hospital: [
    "hospitalName",
    "hospitalAddress",
    "hospitalCity",
    "hospitalState",
    "hospitalZip",
    "hospitalPhone",
    "hospitalFax",
    "hospitalType",
  ],
  diagnosis: [],
  referral: [],
  insuranceInfo: [],
  physicianInfo: [],
  emergency: [],
};

/** Converts an mm/dd/yyyy or yyyy-mm-dd date row value to ISO for the API. */
const rowDateIso = (value) => (nonEmpty(value) ? toIsoDate(value) : null);

export const pickSectionPayload = (data, section) => {
  if (section === "emergency") {
    const contacts = (data.emergencyContacts || [])
      .filter((c) => nonEmpty(c.firstName) || nonEmpty(c.lastName))
      .map((c) => ({
        firstName: c.firstName.trim(),
        lastName: c.lastName.trim(),
        relationship: nonEmpty(c.relationship) ? c.relationship.trim() : null,
        phone: nonEmpty(c.phone) ? c.phone.trim() : null,
      }));
    return { emergencyContacts: contacts };
  }

  if (section === "diagnosis") {
    const rawDx = Array.isArray(data.diagnosisAllergies)
      ? data.diagnosisAllergies
      : [];
    const rawAllergies = Array.isArray(data.allergies) ? data.allergies : [];
    const diagnosisAllergies = rawDx
      .filter(
        (d) =>
          nonEmpty(d.primaryDiagnosis) ||
          nonEmpty(d.diagnosisCode) ||
          nonEmpty(d.clinicalGroup),
      )
      .map((d) => ({
        diagnosisCode: nonEmpty(d.diagnosisCode)
          ? d.diagnosisCode.trim()
          : null,
        primaryDiagnosis: nonEmpty(d.primaryDiagnosis)
          ? d.primaryDiagnosis.trim()
          : null,
        clinicalGroup: nonEmpty(d.clinicalGroup)
          ? d.clinicalGroup.trim()
          : null,
        isBillable:
          typeof d.isBillable === "boolean" ? d.isBillable : null,
      }));
    return {
      diagnosisAllergies,
      allergies: serializeAllergies(rawAllergies),
      nutrition: serializeNutrition(data.nutrition || {}),
    };
  }

  if (section === "referral") {
    const rows = (data.referrals || [])
      .filter(
        (r) =>
          nonEmpty(r.referralDate) ||
          nonEmpty(r.referralSource) ||
          nonEmpty(r.referralName),
      )
      .map((r) => ({
        referralDate: rowDateIso(r.referralDate),
        referralSource: nonEmpty(r.referralSource)
          ? r.referralSource.trim()
          : null,
        referralName: nonEmpty(r.referralName) ? r.referralName.trim() : null,
      }));
    return { referrals: rows };
  }

  if (section === "insuranceInfo") {
    const rows = (data.insurances || [])
      .filter((i) => nonEmpty(i.insuranceName))
      .map((i) => ({
        startDate: rowDateIso(i.startDate),
        insuranceName: i.insuranceName.trim(),
        policyNumber: nonEmpty(i.policyNumber) ? i.policyNumber.trim() : null,
        insuranceGroup: nonEmpty(i.insuranceGroup)
          ? i.insuranceGroup.trim()
          : null,
        patientCoPay: nonEmpty(i.patientCoPay) ? i.patientCoPay.trim() : null,
      }));
    return { insurances: rows };
  }

  if (section === "physicianInfo") {
    const rows = (data.physicians || [])
      .filter((p) => nonEmpty(p.physicianName))
      .map((p) => ({
        physicianType: nonEmpty(p.physicianType) ? p.physicianType : null,
        physicianName: p.physicianName.trim(),
        physicianAddress: nonEmpty(p.physicianAddress)
          ? p.physicianAddress.trim()
          : null,
        npiNumber: nonEmpty(p.npiNumber) ? p.npiNumber.trim() : null,
        pecosStatus:
          p.pecosStatus === "enrolled" || p.pecosStatus === "un-enrolled"
            ? p.pecosStatus
            : null,
        physicianPhone: nonEmpty(p.physicianPhone)
          ? p.physicianPhone.trim()
          : null,
        physicianEmail: nonEmpty(p.physicianEmail)
          ? p.physicianEmail.trim()
          : null,
        physicianFax: nonEmpty(p.physicianFax) ? p.physicianFax.trim() : null,
      }));
    return { physicians: rows };
  }

  const payload = {};
  SECTION_KEYS[section].forEach((key) => {
    const value = data[key];
    payload[key] =
      typeof value === "string" && value.trim() === "" ? null : value;
  });

  if (section === "personal") {
    // Empty optional text must be null so clear-on-edit persists (not omitted).
    payload.middleName = nonEmpty(data.middleName)
      ? String(data.middleName).trim()
      : null;
    payload.dateOfBirth = data.dateOfBirth ? toIsoDate(data.dateOfBirth) : null;
    payload.admissionDate = data.admissionDate
      ? toIsoDate(data.admissionDate)
      : null;
    payload.status = data.status || "Admit";
    payload.isActive =
      payload.status === "Admit" || payload.status === "Active";
    if (
      payload.status === "Inactive" ||
      payload.status === "Discharged"
    ) {
      payload.eocDate = data.eocDate ? toIsoDate(data.eocDate) : null;
    } else {
      payload.eocDate = null;
    }
    payload.gender = data.gender || null;
    payload.maritalStatus = data.maritalStatus || null;
    payload.language = nonEmpty(data.language)
      ? String(data.language).trim()
      : null;
    payload.race = nonEmpty(data.race) ? String(data.race).trim() : null;
    payload.religion = nonEmpty(data.religion)
      ? String(data.religion).trim()
      : null;
  }

  if (section === "contact" && payload.zip) {
    payload.zip = formatZip(payload.zip);
  }

  if (section === "pharmacy") {
    payload.pharmacyZip = payload.pharmacyZip
      ? formatZip(payload.pharmacyZip)
      : null;
    payload.pharmacyName = nonEmpty(payload.pharmacyName)
      ? String(payload.pharmacyName).trim()
      : null;
    payload.pharmacyAddress = nonEmpty(payload.pharmacyAddress)
      ? String(payload.pharmacyAddress).trim()
      : null;
    payload.pharmacyCity = nonEmpty(payload.pharmacyCity)
      ? String(payload.pharmacyCity).trim()
      : null;
    payload.pharmacyState = nonEmpty(payload.pharmacyState)
      ? String(payload.pharmacyState).trim()
      : null;
    payload.pharmacyPhone = nonEmpty(payload.pharmacyPhone)
      ? String(payload.pharmacyPhone).trim()
      : null;
    payload.pharmacyFax = nonEmpty(payload.pharmacyFax)
      ? String(payload.pharmacyFax).trim()
      : null;
    payload.pharmacyType = nonEmpty(payload.pharmacyType)
      ? String(payload.pharmacyType).trim()
      : null;
  }

  if (section === "hospital") {
    payload.hospitalZip = payload.hospitalZip
      ? formatZip(payload.hospitalZip)
      : null;
    payload.hospitalName = nonEmpty(payload.hospitalName)
      ? String(payload.hospitalName).trim()
      : null;
    payload.hospitalAddress = nonEmpty(payload.hospitalAddress)
      ? String(payload.hospitalAddress).trim()
      : null;
    payload.hospitalCity = nonEmpty(payload.hospitalCity)
      ? String(payload.hospitalCity).trim()
      : null;
    payload.hospitalState = nonEmpty(payload.hospitalState)
      ? String(payload.hospitalState).trim()
      : null;
    payload.hospitalPhone = nonEmpty(payload.hospitalPhone)
      ? String(payload.hospitalPhone).trim()
      : null;
    payload.hospitalFax = nonEmpty(payload.hospitalFax)
      ? String(payload.hospitalFax).trim()
      : null;
    payload.hospitalType = nonEmpty(payload.hospitalType)
      ? String(payload.hospitalType).trim()
      : null;
  }

  return payload;
};

/** Section validators matching Add Patient rules */
export const validateDemographicsSection = (section, values) => {
  const errors = {};

  if (section === "personal") {
    if (!nonEmpty(values.lastName)) errors.lastName = "Last name is required";
    if (!nonEmpty(values.firstName)) errors.firstName = "First name is required";
    if (!nonEmpty(values.dateOfBirth)) {
      errors.dateOfBirth = "Date of birth is required";
    }
    if (values.gender !== "Male" && values.gender !== "Female") {
      errors.gender = "Gender is required (Male or Female)";
    }
    if (!nonEmpty(values.admissionDate)) {
      errors.admissionDate = "SOC date is required";
    }
    if (
      (values.status === "Inactive" || values.status === "Discharged") &&
      !nonEmpty(values.eocDate)
    ) {
      errors.eocDate = "EOC date is required when status is Inactive or Discharged";
    }
  }

  if (section === "contact") {
    if (!nonEmpty(values.phone) || !US_PHONE.test(values.phone)) {
      errors.phone = "Primary phone is required ((XXX)XXX-XXXX)";
    }
    if (values.alternatePhone?.trim() && !US_PHONE.test(values.alternatePhone)) {
      errors.alternatePhone = "Alternate phone must be (XXX)XXX-XXXX";
    }
    if (!nonEmpty(values.address)) errors.address = "Street address is required";
    if (!nonEmpty(values.addressLine2)) {
      errors.addressLine2 = "Apt / Suite is required";
    }
    if (!nonEmpty(values.state)) errors.state = "State is required";
    if (!nonEmpty(values.zip) || !US_ZIP.test(values.zip)) {
      errors.zip = "Zip must be XXXXX or XXXXX-XXXX";
    }
  }

  if (section === "pharmacy") {
    if (values.pharmacyZip?.trim() && !US_ZIP.test(values.pharmacyZip)) {
      errors.pharmacyZip = "Zip must be XXXXX or XXXXX-XXXX";
    }
    if (values.pharmacyPhone?.trim() && !US_PHONE.test(values.pharmacyPhone)) {
      errors.pharmacyPhone = "Phone must be (XXX)XXX-XXXX";
    }
    if (values.pharmacyFax?.trim() && !US_PHONE.test(values.pharmacyFax)) {
      errors.pharmacyFax = "Fax must be (XXX)XXX-XXXX";
    }
  }

  if (section === "hospital") {
    if (values.hospitalZip?.trim() && !US_ZIP.test(values.hospitalZip)) {
      errors.hospitalZip = "Zip must be XXXXX or XXXXX-XXXX";
    }
    if (values.hospitalPhone?.trim() && !US_PHONE.test(values.hospitalPhone)) {
      errors.hospitalPhone = "Phone must be (XXX)XXX-XXXX";
    }
    if (values.hospitalFax?.trim() && !US_PHONE.test(values.hospitalFax)) {
      errors.hospitalFax = "Fax must be (XXX)XXX-XXXX";
    }
  }

  if (section === "emergency") {
    const contacts = Array.isArray(values.emergencyContacts)
      ? values.emergencyContacts
      : [];
    for (let idx = 0; idx < contacts.length; idx += 1) {
      const c = contacts[idx];
      if (!nonEmpty(c.firstName) || !nonEmpty(c.lastName)) {
        errors.emergencyContacts = `Contact ${idx + 1}: first and last name are required`;
        break;
      }
      if (c.phone?.trim() && !US_PHONE.test(c.phone)) {
        errors.emergencyContacts = `Contact ${idx + 1}: phone must be (XXX)XXX-XXXX`;
        break;
      }
    }
  }

  if (section === "diagnosis") {
    const incomplete = findIncompleteOtherAllergy(values.allergies);
    if (incomplete) {
      errors.allergies = incomplete.message;
    }
    const nutritionError = validateNutritionFields(values.nutrition);
    if (nutritionError) {
      errors.nutrition = nutritionError;
    }
  }

  if (section === "insuranceInfo") {
    const rows = (Array.isArray(values.insurances) ? values.insurances : []).filter(
      (i) => nonEmpty(i.insuranceName) || nonEmpty(i.policyNumber),
    );
    if (!rows.length) {
      errors.insurances = "At least one insurance is required";
    } else {
      for (let idx = 0; idx < rows.length; idx += 1) {
        const row = rows[idx];
        if (!nonEmpty(row.insuranceName)) {
          errors.insurances = `Insurance ${idx + 1}: insurance name is required`;
          break;
        }
        if (!nonEmpty(row.policyNumber)) {
          errors.insurances = `Insurance ${idx + 1}: policy number is required`;
          break;
        }
      }
    }
  }

  if (section === "physicianInfo") {
    const rows = Array.isArray(values.physicians) ? values.physicians : [];
    for (let idx = 0; idx < rows.length; idx += 1) {
      const row = rows[idx];
      const touched =
        nonEmpty(row.physicianName) ||
        nonEmpty(row.physicianAddress) ||
        nonEmpty(row.physicianPhone) ||
        nonEmpty(row.physicianFax) ||
        nonEmpty(row.physicianEmail) ||
        nonEmpty(row.npiNumber) ||
        nonEmpty(row.physicianType);
      if (!touched) continue;

      if (!nonEmpty(row.physicianName)) {
        errors[`physicians.${idx}.physicianName`] =
          "Physician name is required";
      }
      if (row.physicianPhone?.trim() && !US_PHONE.test(row.physicianPhone)) {
        errors[`physicians.${idx}.physicianPhone`] =
          "Phone must be (XXX)XXX-XXXX";
      }
      if (row.physicianFax?.trim() && !US_PHONE.test(row.physicianFax)) {
        errors[`physicians.${idx}.physicianFax`] =
          "Fax must be (XXX)XXX-XXXX";
      }
      if (row.physicianEmail?.trim() && !EMAIL_RE.test(row.physicianEmail.trim())) {
        errors[`physicians.${idx}.physicianEmail`] =
          "Invalid email format";
      }
    }
  }

  if (section === "referral") {
    const rows = Array.isArray(values.referrals) ? values.referrals : [];
    for (let idx = 0; idx < rows.length; idx += 1) {
      const row = rows[idx];
      if (row.referralSource === "Other" && !nonEmpty(row.referralName)) {
        errors.referrals = `Referral ${idx + 1}: referral name is required for "Other"`;
        break;
      }
    }
  }

  return errors;
};

/** Staff contact subtitle: "Phone : (747)313-1977, Email : name@mail.com" */
export const formatStaffContactLine = (member = {}) => {
  const mobileRaw = member.mobileNumber || member.phone || "";
  const digits = String(mobileRaw).replaceAll(/\D/g, "");
  let mobile = "";
  if (digits.length >= 10) {
    const d = digits.slice(-10);
    mobile = `(${d.slice(0, 3)})${d.slice(3, 6)}-${d.slice(6)}`;
  } else if (mobileRaw) {
    mobile = mobileRaw;
  }
  const email = member.email || "";
  if (mobile && email) return `Phone : ${mobile}, Email : ${email}`;
  if (mobile) return `Phone : ${mobile}`;
  if (email) return `Email : ${email}`;
  return "";
};
